import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { StyleSelector } from '@/components/StyleSelector';
import { CreationModeSelector } from '@/components/CreationModeSelector';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, FileText, Loader2, Sparkles, Check,
  Wand2, PenLine, Film, ChevronRight, Clapperboard
} from 'lucide-react';

const ANALYSIS_MESSAGES = [
  'Reading through your document...',
  'Identifying every character and their visual traits...',
  'Mapping out story arcs, settings, and environments...',
  'Extracting mood, atmosphere, and color cues...',
  'Breaking down scene structures and pacing...',
  'Analyzing dialogue patterns and emotional beats...',
  'Building comprehensive character profiles...',
  'Cross-referencing character appearances across scenes...',
  'Finalizing production analysis...',
];

export default function ProjectWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0=create, 1=analyzing, 2=choose
  const [projectId, setProjectId] = useState(null);

  // Step 1: Create
  const [name, setName] = useState('');
  const [visualStyle, setVisualStyle] = useState('photorealistic');
  const [creationMode, setCreationMode] = useState('advanced');
  const [simpleScenes, setSimpleScenes] = useState([]);
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [animMsgIdx, setAnimMsgIdx] = useState(0);
  const [characters, setCharacters] = useState([]);
  const [expandedChar, setExpandedChar] = useState(null);
  const [editCharDescs, setEditCharDescs] = useState({});
  const fileInputRef = useRef(null);

  // Animate analysis messages
  useEffect(() => {
    if (!analyzing) return;
    const interval = setInterval(() => {
      setAnimMsgIdx(i => (i + 1) % ANALYSIS_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [analyzing]);

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Project name is required'); return; }
    setCreating(true);
    try {
      const res = await api.post('/projects', { name: name.trim(), description: '', visual_style: visualStyle, aspect_ratio: aspectRatio, creation_mode: creationMode });
      setProjectId(res.data.id);
      toast.success('Project created!');
    } catch { toast.error('Failed to create project'); }
    finally { setCreating(false); }
  };

  const [analysisProgress, setAnalysisProgress] = useState('');

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !projectId) return;
    setUploading(true);
    setAnalyzing(true);
    setAnimMsgIdx(0);
    setAnalysisProgress('');
    setStep(1);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/projects/${projectId}/analyze-script`, formData, { timeout: 180000 });
      const jobId = res.data.job_id;
      
      // Poll script-status until complete — 20 min max for very long documents
      let attempts = 0;
      const maxAttempts = 240;
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 5000));
        attempts++;
        try {
          const statusRes = await api.get(`/projects/${projectId}/script-status`, { params: { _t: Date.now() } });
          const job = statusRes.data;
          if (job.progress) setAnalysisProgress(job.progress);
          if (job.status === 'complete') {
            if (job.simple) {
              setSimpleScenes(job.created_scenes || []);
              toast.success(`${(job.created_scenes || []).length} scenes created — prompts only, no characters or locations`);
              setStep(2);
              return;
            }
            const charsRes = await api.get(`/projects/${projectId}/characters`);
            setCharacters(charsRes.data);
            toast.success(`Analysis complete! Found ${charsRes.data.length} characters`);
            setStep(2);
            return;
          } else if (job.status === 'failed') {
            toast.error(job.error || 'Script analysis failed');
            setStep(0);
            return;
          }
          // Still processing — continue polling
        } catch {
          // Network error during poll, keep trying
        }
      }
      toast.error('Analysis timed out. Please try with a shorter document or try again.');
      setStep(0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
      setStep(0);
    } finally {
      setUploading(false);
      setAnalyzing(false);
      setAnalysisProgress('');
    }
  };

  const handleEnterProduction = async () => {
    try {
      await api.put(`/projects/${projectId}`, { production_mode: true, current_scene_index: 0 });
      navigate(`/project/${projectId}/production`);
    } catch { toast.error('Failed to enter production mode'); }
  };

  return (
    <div className="min-h-screen bg-[#060608]">
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-sm text-[#8A8A93] hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </button>

        {/* ═══ STEP 1: CREATE + UPLOAD ═══ */}
        {step === 0 && (
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>New Project</h1>
              <p className="text-sm text-[#8A8A93]">Name your project and upload your script or document</p>
            </div>

            {/* Project Name */}
            <div className="ff-card p-6">
              <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF4500] block mb-3">Project Name</label>
              <input data-testid="project-name" value={name} onChange={e => setName(e.target.value)}
                className="ff-input w-full text-lg" placeholder="e.g. The Last Frontier" autoFocus />
            </div>

            {/* Creation mode */}
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#A78BFA] block mb-3">How do you want to build scenes?</label>
              <CreationModeSelector value={creationMode} onChange={setCreationMode} />
            </div>

            {/* Visual Style Selector */}
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#A78BFA] block mb-3">Visual Style</label>
              <StyleSelector value={visualStyle} onChange={setVisualStyle} columns="grid-cols-2 sm:grid-cols-3" />
            </div>

            {/* Aspect Ratio Selector */}
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#00F0FF] block mb-3">Output Format</label>
              <div className="grid grid-cols-2 gap-3" data-testid="aspect-ratio-selector">
                <button data-testid="aspect-16-9" onClick={() => setAspectRatio('16:9')}
                  className={`p-4 rounded-lg border text-center transition-all ${
                    aspectRatio === '16:9'
                      ? 'bg-[#00F0FF]/10 border-[#00F0FF]/40 text-white'
                      : 'bg-white/[0.02] border-white/[0.06] text-[#8A8A93] hover:border-white/20 hover:text-white'
                  }`}>
                  <div className={`w-16 h-9 mx-auto mb-2 rounded border-2 ${aspectRatio === '16:9' ? 'border-[#00F0FF]' : 'border-[#55555C]'}`} />
                  <span className="text-sm font-medium block">Landscape</span>
                  <span className="text-[10px] text-[#55555C]">16:9 — YouTube, TV</span>
                </button>
                <button data-testid="aspect-9-16" onClick={() => setAspectRatio('9:16')}
                  className={`p-4 rounded-lg border text-center transition-all ${
                    aspectRatio === '9:16'
                      ? 'bg-[#00F0FF]/10 border-[#00F0FF]/40 text-white'
                      : 'bg-white/[0.02] border-white/[0.06] text-[#8A8A93] hover:border-white/20 hover:text-white'
                  }`}>
                  <div className={`w-9 h-16 mx-auto mb-2 rounded border-2 ${aspectRatio === '9:16' ? 'border-[#00F0FF]' : 'border-[#55555C]'}`} />
                  <span className="text-sm font-medium block">Portrait</span>
                  <span className="text-[10px] text-[#55555C]">9:16 — TikTok, Reels</span>
                </button>
              </div>
            </div>

            {/* Document Upload */}
            {projectId ? (
              <div className="ff-card p-8">
                <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#10B981] block mb-4 flex items-center gap-2">
                  <Upload className="w-3.5 h-3.5" /> Upload Script / Document
                </label>
                <input ref={fileInputRef} type="file" accept=".pdf,.docx,.doc,.txt,.rtf" onChange={handleFileUpload} className="hidden" />
                <button data-testid="upload-doc-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  className="w-full py-12 rounded-xl border-2 border-dashed border-white/[0.08] hover:border-[#10B981]/30 transition-all flex flex-col items-center justify-center gap-3 group">
                  {uploading ? (
                    <Loader2 className="w-10 h-10 text-[#10B981] animate-spin" />
                  ) : (
                    <>
                      <FileText className="w-10 h-10 text-[#55555C] group-hover:text-[#10B981] transition-colors" />
                      <span className="text-sm text-[#8A8A93] group-hover:text-white transition-colors">Click to upload PDF, DOCX, or TXT</span>
                      <span className="text-[10px] text-[#55555C]">{creationMode === 'simple' ? 'Simple mode: we only split it into scenes with an overall prompt plus opening/closing frame descriptions' : 'The system will analyze your entire document — characters, locations, scenes and dialogue'}</span>
                    </>
                  )}
                </button>
              </div>
            ) : (
              <button data-testid="create-project-btn" onClick={handleCreate} disabled={creating || !name.trim()}
                className="ff-btn-primary w-full py-4 text-base flex items-center justify-center gap-2 disabled:opacity-40">
                {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : <ChevronRight className="w-5 h-5" />}
                {creating ? 'Creating...' : 'Create Project'}
              </button>
            )}

            {projectId && (
              <p className="text-center text-[10px] text-[#55555C]">
                Or <button onClick={() => navigate(`/project/${projectId}`)} className="text-[#FF4500] hover:underline">skip to manual setup</button>
              </p>
            )}
          </div>
        )}

        {/* ═══ STEP 2: ANALYZING ═══ */}
        {step === 1 && (
          <div className="flex flex-col items-center justify-center py-20">
            {/* Animated pulse ring */}
            <div className="relative mb-8">
              <div className="w-24 h-24 rounded-full bg-[#FF4500]/10 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-[#FF4500]/20 flex items-center justify-center animate-pulse">
                  <Sparkles className="w-8 h-8 text-[#FF4500]" />
                </div>
              </div>
              <div className="absolute inset-0 rounded-full border-2 border-[#FF4500]/20 animate-ping" />
            </div>

            <h2 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Analyzing Your Document</h2>
            <p className="text-sm text-[#8A8A93] mb-6 text-center max-w-md">
              Our AI is performing a comprehensive analysis of your script — identifying every character, setting, and scene.
            </p>

            {/* Animated message */}
            <div className="h-8 flex items-center">
              <p className="text-xs text-[#FF4500] animate-pulse flex items-center gap-2" key={animMsgIdx}>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {ANALYSIS_MESSAGES[animMsgIdx]}
              </p>
            </div>

            {/* Real-time backend progress */}
            {analysisProgress && (
              <p className="text-[11px] text-[#8A8A93] mt-2" data-testid="analysis-progress">{analysisProgress}</p>
            )}

            {/* Progress dots */}
            <div className="flex gap-1.5 mt-8">
              {ANALYSIS_MESSAGES.map((_, i) => (
                <div key={i} className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${i <= animMsgIdx ? 'bg-[#FF4500]' : 'bg-white/[0.08]'}`} />
              ))}
            </div>
          </div>
        )}

        {/* ═══ STEP 3: CHOOSE MODE ═══ */}
        {step === 2 && creationMode === 'simple' && (
          <div className="space-y-8" data-testid="simple-analysis-complete">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-[#10B981]" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Scenes Ready</h2>
              <p className="text-sm text-[#8A8A93]">
                Split into <span className="text-white font-semibold">{simpleScenes.length} scene{simpleScenes.length === 1 ? '' : 's'}</span>, each with a scene prompt and opening/closing frame descriptions. No characters or locations were extracted (Simple mode).
              </p>
            </div>
            {simpleScenes.length > 0 && (
              <div className="ff-card p-5">
                <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#55555C] mb-3">Scenes</p>
                <ol className="space-y-1.5">
                  {simpleScenes.map((s, i) => (
                    <li key={s.id} data-testid={`simple-scene-${s.id}`} className="flex items-center gap-3 text-sm text-white px-3 py-2 rounded-lg bg-[#0a0a0e] border border-white/[0.06]">
                      <span className="text-[10px] font-mono text-[#8A8A93] w-5">{i + 1}</span>{s.title}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              <button data-testid="simple-enter-production" onClick={handleEnterProduction} className="ff-btn-primary flex-1 py-3 flex items-center justify-center gap-2"><Clapperboard className="w-4 h-4" /> Start producing</button>
              <button data-testid="simple-open-project" onClick={() => navigate(`/project/${projectId}`)} className="ff-btn-secondary flex-1 py-3">Open project</button>
            </div>
          </div>
        )}

        {step === 2 && creationMode !== 'simple' && (
          <div className="space-y-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-[#10B981]" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Analysis Complete</h2>
              <p className="text-sm text-[#8A8A93]">
                Found <span className="text-white font-semibold">{characters.length} characters</span> in your document
              </p>
            </div>

            {/* Character preview — editable descriptions */}
            {characters.length > 0 && (
              <div className="ff-card p-5">
                <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#55555C] mb-3">Characters Identified — Click to edit descriptions</p>
                <div className="space-y-3">
                  {characters.map(c => (
                    <div key={c.id} className="rounded-lg border border-white/[0.06] bg-[#0a0a0e] overflow-hidden">
                      <button
                        data-testid={`toggle-char-${c.id}`}
                        onClick={() => setExpandedChar(expandedChar === c.id ? null : c.id)}
                        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-white/[0.02] transition-colors"
                      >
                        <span className="text-sm font-medium text-white">{c.name}</span>
                        <ChevronRight className={`w-4 h-4 text-[#55555C] transition-transform ${expandedChar === c.id ? 'rotate-90' : ''}`} />
                      </button>
                      {expandedChar === c.id && (
                        <div className="px-4 pb-4 border-t border-white/[0.04]">
                          <label className="text-[10px] text-[#8A8A93] block mt-3 mb-1.5">Character Description</label>
                          <textarea
                            data-testid={`char-desc-${c.id}`}
                            value={editCharDescs[c.id] ?? c.description ?? ''}
                            onChange={e => setEditCharDescs(prev => ({ ...prev, [c.id]: e.target.value }))}
                            className="ff-input w-full h-32 resize-y text-xs leading-relaxed"
                            placeholder="Physical appearance, personality, wardrobe..."
                          />
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-[10px] text-[#55555C]">{(editCharDescs[c.id] ?? c.description ?? '').length} chars</span>
                            <button
                              data-testid={`save-char-desc-${c.id}`}
                              onClick={async () => {
                                const desc = editCharDescs[c.id] ?? c.description;
                                try {
                                  await api.put(`/projects/${projectId}/characters/${c.id}`, { description: desc });
                                  setCharacters(prev => prev.map(ch => ch.id === c.id ? { ...ch, description: desc } : ch));
                                  toast.success(`${c.name} updated!`);
                                } catch { toast.error('Failed to save'); }
                              }}
                              className="text-xs px-3 py-1.5 rounded-lg bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25 border border-[#10B981]/30 flex items-center gap-1.5"
                            >
                              <Check className="w-3 h-3" /> Save
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mode selection */}
            <div className="grid grid-cols-1 gap-4">
              <button data-testid="mode-ai-assistant" onClick={handleEnterProduction}
                className="ff-card p-8 text-left hover:border-[#FF4500]/30 hover:bg-[#FF4500]/5 transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#FF4500]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#FF4500]/20 transition-colors">
                    <Wand2 className="w-6 h-6 text-[#FF4500]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">AI-Assisted Scene by Scene</h3>
                    <p className="text-xs text-[#8A8A93] leading-relaxed">
                      Your AI assistant will walk you through each scene one at a time — identifying the setting, cast, dialogue, and generating everything step by step.
                    </p>
                  </div>
                </div>
              </button>

              <button data-testid="mode-manual" onClick={() => navigate(`/project/${projectId}`)}
                className="ff-card p-8 text-left hover:border-white/[0.15] hover:bg-white/[0.02] transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/[0.04] flex items-center justify-center flex-shrink-0 group-hover:bg-white/[0.08] transition-colors">
                    <PenLine className="w-6 h-6 text-[#8A8A93]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">Manual Creation</h3>
                    <p className="text-xs text-[#8A8A93] leading-relaxed">
                      Create and manage everything yourself — add characters, build scenes, and generate content at your own pace.
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
